/**
 * quiz-runtime bootstrap.
 *
 * Routing on URL params:
 *   ?preview=true          → Studio preview handler — listens
 *                            for QUIZ_PREVIEW_CONFIG_UPDATED from allowlisted
 *                            Studio origins, re-renders live.
 *   ?sample=1 | =chat      → DEV ONLY — bypass loader, validate
 *                            a local fixture. Tree-shaken from production.
 *   ?pub=X&id=Y (default)  → production — fetch the published
 *                            config from CDN, validate, mount.
 */

import { renderError } from '@/runtime/errors';
import { loadQuiz, validateConfig } from '@/runtime/loader';
import { startPreviewMode } from '@/runtime/preview';
import { mountQuiz } from '@/runtime/render';

async function bootstrap(): Promise<void> {
  const root = document.getElementById('quiz-root');
  if (!root) {
    console.error('[quiz-runtime] #quiz-root element not found');
    return;
  }

  const params = new URLSearchParams(window.location.search);

  if (params.get('preview') === 'true') {
    startPreviewMode(root);
    console.info('[quiz-runtime] preview mode active; waiting for Studio messages');
    return;
  }

  // Dev-only fixture path. The static `import.meta.env.DEV` constant is
  // `false` in production, so this whole branch is tree-shaken out.
  if (import.meta.env.DEV) {
    const sample = params.get('sample');
    if (sample === '1' || sample === 'chat') {
      const mod = await import('@/runtime/dev-sample-config');
      const raw = sample === 'chat' ? mod.CHAT_SAMPLE_CONFIG : mod.SAMPLE_CONFIG;
      const validated = validateConfig(raw);
      if (!validated.ok) {
        renderError(root, validated.error);
        return;
      }
      console.info(`[quiz-runtime] DEV: mounting fixture ?sample=${sample}`);
      await mountQuiz(root, validated.value);
      return;
    }
  }

  const result = await loadQuiz(window.location.search);
  if (!result.ok) {
    renderError(root, result.error);
    return;
  }
  console.info(
    `[quiz-runtime] quiz ${result.value.quizId} revision ${result.value.revision} mounting`,
  );
  await mountQuiz(root, result.value);
}

void bootstrap();
